"use client";
import Typography from "@/components/common/typography";
import { BlockProHighlightList } from "@/types/schema";
import CheckmarkListItem from "@/components/common/lists/checkmark_list_item";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import { Heading } from "@/components/common/typography/Heading";
import { DirectusFile } from "@directus/sdk";
import { ListType } from "@/types/homepage_types";
import Image from "next/image";

interface EmployeeTextProps {
  description: string;
  description_1: string;
  list_title: string;
  list_title_1: string;
  list: ListType[];
  list_1: ListType[];
  cta_label: string;
  cta_link: string;
  blue?: boolean;
}

const EmployeeText = ({
  description,
  description_1,
  list,
  list_1,
  cta_label,
  cta_link,
  list_title,
  list_title_1,
  blue = false,
}: EmployeeTextProps) => {
  return (
    <section className="flex flex-col w-full justify-between ">
      <section className="flex flex-col gap-[0.938rem] mb-2.5 sm:mb-35px">
        <Typography className="lg:block hidden" size="body1" weight={300}>
          Parham K. has been detailing cars since 2010 and has personally
          restored over 3,000 vehicles. With 15+ years of hands-on experience,
          he’s trained in advanced paint correction, ceramic coating
          application, and full interior and exterior restoration.
        </Typography>
        <Typography className="lg:block hidden" size="body1" weight={300}>
          IDA-certified and meticulous by nature, Parham sets the gold standard
          for every pro on the Panda Hub platform. He leads with care,
          consistency, and real craftsmanship. “It’s not just the clean that
          matters. It’s how people feel when they see their car again.”
        </Typography>

        <Typography
          color="copyGray"
          className="lg:hidden block [&_strong]:font-medium"
        >
          Parham K. is IDA-certified with 15+ years of experience and over 3,000
          vehicles restored. He leads detailing excellence at Panda Hub,{" "}
          <strong>training every pro on the platform to his standard.</strong>{" "}
          &quot;It&apos;s not just the clean that matters. It&apos;s how people
          feel when they see their car again.&quot;
        </Typography>
      </section>

      <section className="lg:mb-0 mb-35px ">
        <div className="grid grid-cols-1 sm:grid-cols-2 max-w-[43.563rem] gap-[0.938rem] sm:flex-row flex-col items-start">
          {/* LIST 1 */}
          <section className="flex flex-col gap-[.9375rem]">
            <Heading as="p" size="h6">
              {list_title}
            </Heading>
            <ul className="max-w-[20.5625rem] gap-5px flex flex-col">
              {list.map((item, index) => {
                {
                  /* Use a list item for the image */
                }
                const intrinsicWidth = item.item.icon?.width || 70;
                const intrinsicHeight = item.item.icon?.height || 30;
                return (
                  <li key={index} className="pui-list-item-with-check-icon">
                    <section>
                      <Typography size="body1">{item.item.title}</Typography>
                    </section>
                    {item.item.icon && (
                      <div className="min-w-0 h-7">
                        <Image
                          src={item.item.icon.filename_disk! || ""}
                          alt={item.item.icon.description || "Featured logo"}
                          width={intrinsicWidth}
                          height={intrinsicHeight}
                          loading="lazy"
                          sizes="(max-width: 1024px) 25vw, 12vw"
                          className="object-contain select-none"
                          draggable={false}
                        />
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>

          {/* LIST 2 */}
          <section className="hidden lg:flex flex-col gap-[.9375rem]">
            <Heading as="p" size="h6">
              Expertise and Specializations
              {/* {list_title_1} */}
            </Heading>

            <ul className=" gap-5px flex flex-col">
              {list_1.map((item, index) => (
                <li key={index} className="pui-list-item-with-check-icon">
                  <section className="">
                    <Typography size="body1">{item.item.title}</Typography>
                  </section>
                  {item.item.icon && (
                    <div className="min-w-0 relative h-7">
                      <Image
                        src={item.item.icon.filename_disk! || ""}
                        alt={item.item.icon.description || "Featured logo"}
                        fill
                        loading="lazy"
                        sizes="(max-width: 1024px) 25vw, 12vw"
                        className="object-contain select-none"
                        draggable={false}
                      />
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </section>
        </div>
      </section>

      <Link
        className="w-full hidden lg:flex lg:justify-end cursor-pointer lg:-mt-10"
        href={`/${cta_link}`}
      >
        <Button
          size={"lg"}
          className={`${blue ? "bg-[#186CFF] hover:bg-[#0051DD]" : ""}`}
          variant={"default"}
        >
          <Typography size="unresponsiveBody1" weight={500}>
            {cta_label}
          </Typography>
        </Button>
      </Link>
    </section>
  );
};

export default EmployeeText;
